<template>
  <div class="sales-analysis-shell">
    <!-- HERO -->
    <section class="analysis-hero">
      <div>
        <p class="eyebrow">LotLedger Wine Bar</p>
        <h1>Sales Analysis</h1>
        <p class="hero-copy">
          Review sales quantity, revenue, COGS, gross profit, and margin by category or item.
        </p>
      </div>

      <button @click="fetchReport" class="primary-action">
        Generate Report
      </button>
    </section>

    <!-- FILTERS -->
    <section class="analysis-toolbar">
      <label>
        <span>Filter By</span>
        <select v-model="filterBy">
          <option value="category">Category</option>
          <option value="item_no">Item Number</option>
        </select>
      </label>

      <label v-if="filterBy === 'category'">
        <span>Category</span>
        <select v-model="filterValue">
          <option value="">All Categories</option>
          <option
            v-for="category in categories"
            :key="category"
            :value="category"
          >
            {{ category }}
          </option>
        </select>
      </label>

      <label v-if="filterBy === 'item_no'">
        <span>Item Number</span>
        <input
          type="text"
          v-model="filterValue"
          placeholder="Enter Item Number"
        />
      </label>

      <label>
        <span>Start Date</span>
        <input type="date" v-model="startDate" />
      </label>

      <label>
        <span>End Date</span>
        <input type="date" v-model="endDate" />
      </label>

      <button type="button" @click="setToday" class="ghost-action">
        Today
      </button>

      <button type="button" @click="setThisMonth" class="ghost-action">
        This Month
      </button>
    </section>

    <!-- SUMMARY -->
    <section class="summary-grid">
      <article class="summary-card">
        <span>Total Revenue</span>
        <strong>${{ money(totalRevenue) }}</strong>
      </article>

      <article class="summary-card">
        <span>Total COGS</span>
        <strong>${{ money(totalCogs) }}</strong>
      </article>

      <article class="summary-card gold">
        <span>Gross Profit</span>
        <strong>${{ money(totalGrossProfit) }}</strong>
      </article>

      <article class="summary-card">
        <span>Gross Margin</span>
        <strong :class="{ negative: totalGrossMargin < 0 }">
          {{ percent(totalGrossMargin) }}
        </strong>
      </article>
    </section>

    <!-- REPORT -->
    <section class="report-card">
      <div class="report-header">
        <div>
          <p class="eyebrow">Report</p>
          <h2>{{ filterBy === 'category' ? 'Category Performance' : 'Item Performance' }}</h2>
        </div>

        <span class="status-pill">
          {{ reportData.length }} row{{ reportData.length === 1 ? '' : 's' }}
        </span>
      </div>

      <div v-if="reportData.length" class="table-wrap">
        <table class="analysis-table">
          <thead>
            <tr>
              <th class="left-align">
                {{ filterBy === 'category' ? 'Category' : 'Item Number' }}
              </th>
              <th class="right-align">Sales Qty</th>
              <th class="right-align">Revenue</th>
              <th class="right-align">COGS</th>
              <th class="right-align">Gross Profit</th>
              <th class="right-align">Gross Profit %</th>
              <th class="right-align">Last Sale</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="item in reportData"
              :key="`${filterBy}-${item.item_no || item.category_name}`"
            >
              <td class="left-align strong-cell">
                {{ filterBy === 'category' ? item.category_name : item.item_no }}
              </td>

              <td class="right-align">
                {{ number(item.total_sales_qty) }}
              </td>

              <td class="right-align">
                ${{ money(item.total_sales_revenue) }}
              </td>

              <td class="right-align">
                ${{ money(item.total_cogs) }}
              </td>

              <td
                class="right-align"
                :class="{ negative: Number(item.gross_profit || 0) < 0 }"
              >
                ${{ money(item.gross_profit) }}
              </td>

              <td
                class="right-align"
                :class="{ negative: Number(item.gross_profit_percent || 0) < 0 }"
              >
                {{ percent(item.gross_profit_percent) }}
              </td>

              <td class="right-align">
                {{ formatDate(item.last_sale_date) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="empty-state">
        <h3>No sales analysis yet</h3>
        <p>Choose a date range and generate the report.</p>
      </div>
    </section>

    <!-- READOUT -->
    <section class="insight-card">
      <div class="section-heading">
        <p class="eyebrow">Readout</p>
        <h2>Quick Take</h2>
      </div>

      <p>{{ readoutText }}</p>
    </section>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'SalesAnalysisReport',

  data() {
    return {
      filterBy: 'category',
      filterValue: '',
      startDate: '',
      endDate: '',
      reportData: [],
      categories: []
    };
  },

  computed: {
    totalRevenue() {
      return this.reportData.reduce((sum, item) => {
        return sum + Number(item.total_sales_revenue || 0);
      }, 0);
    },

    totalCogs() {
      return this.reportData.reduce((sum, item) => {
        return sum + Number(item.total_cogs || 0);
      }, 0);
    },

    totalGrossProfit() {
      return this.reportData.reduce((sum, item) => {
        return sum + Number(item.gross_profit || 0);
      }, 0);
    },

    totalGrossMargin() {
      return this.totalRevenue > 0
        ? (this.totalGrossProfit / this.totalRevenue) * 100
        : null;
    },

    readoutText() {
      if (!this.reportData.length) {
        return 'Generate the report to see sales performance by category or item.';
      }

      if (this.totalGrossProfit < 0) {
        return 'This range shows negative gross profit. Check costs, returns, inventory adjustments, or test sales.';
      }

      if (this.totalGrossMargin >= 65) {
        return 'This range has a strong gross margin. Review the rows below to see which categories or items are driving the result.';
      }

      return 'This range is profitable, but margin may need monitoring. Look for high revenue rows with low gross profit percentage.';
    }
  },

  watch: {
    filterBy() {
      this.filterValue = '';
      this.reportData = [];
    }
  },

  methods: {
    async fetchCategories() {
      try {
        const response = await api.get('/inventory');

        const uniqueCategories = [
          ...new Set(
            (response.data || [])
              .map(item => item.category_name)
              .filter(Boolean)
          )
        ];

        this.categories = uniqueCategories.sort();
      } catch (error) {
        console.error('Error fetching categories:', error);
        this.categories = [];
      }
    },

    async fetchReport() {
      if (!this.startDate || !this.endDate) {
        alert('Choose a start date and end date first.');
        return;
      }

      try {
        const response = await api.get('/reports/sales-analysis', {
          params: {
            filter_by: this.filterBy,
            filter_value: this.filterValue,
            start_date: this.startDate,
            end_date: this.endDate
          }
        });

        this.reportData = response.data || [];
      } catch (error) {
        console.error('Error fetching Sales Analysis report:', error);
        alert('Failed to fetch Sales Analysis report. Check the backend logs.');
      }
    },

    setToday() {
      const today = new Date().toISOString().split('T')[0];

      this.startDate = today;
      this.endDate = today;
      this.fetchReport();
    },

    setThisMonth() {
      const now = new Date();

      const start = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split('T')[0];

      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];

      this.startDate = start;
      this.endDate = end;
      this.fetchReport();
    },

    money(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    number(value) {
      return Number(value || 0).toLocaleString('en-US', {
        maximumFractionDigits: 4
      });
    },

    percent(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
      }

      return `${Number(value || 0).toFixed(2)}%`;
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      return new Date(dateString).toLocaleDateString('en-US');
    }
  },

  mounted() {
    this.fetchCategories();
    this.setToday();
  }
};
</script>

<style scoped>
.sales-analysis-shell {
  min-height: 100vh;
  padding: 28px;
  color: #fff8f1;
  font-family: "Poppins", sans-serif;
  background:
    radial-gradient(circle at top left, rgba(128, 18, 54, 0.28), transparent 34%),
    linear-gradient(135deg, #120b10, #22131a 48%, #0f0a0d);
}

.analysis-hero,
.analysis-toolbar,
.summary-card,
.report-card,
.insight-card {
  border: 1px solid rgba(255, 255, 255, 0.095);
  background: rgba(255, 255, 255, 0.065);
  box-shadow: 0 20px 55px rgba(0, 0, 0, 0.22);
}

.analysis-hero {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;
  padding: 26px;
  margin-bottom: 18px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.035));
}

.eyebrow {
  margin: 0 0 8px;
  color: #c8a66a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.analysis-hero h1 {
  margin: 0;
  font-size: clamp(42px, 6vw, 82px);
  letter-spacing: -0.08em;
  line-height: 0.9;
}

.hero-copy {
  max-width: 680px;
  margin: 14px 0 0;
  color: #d8cbc4;
  line-height: 1.5;
}

.analysis-toolbar {
  display: grid;
  grid-template-columns: 180px 1fr 180px 180px auto auto;
  gap: 14px;
  align-items: end;
  padding: 18px;
  margin-bottom: 16px;
  border-radius: 24px;
}

.analysis-toolbar label {
  display: grid;
  gap: 8px;
}

.analysis-toolbar label span {
  color: #b9aaa4;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

input,
select {
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(8, 5, 8, 0.58);
  color: #fff8f1;
  padding: 0 14px;
  outline: none;
}

.primary-action,
.ghost-action {
  min-height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  font-weight: 900;
}

.primary-action {
  color: #1b1015;
  background: linear-gradient(135deg, #f1c979, #c8a66a);
}

.ghost-action {
  color: #fff8f1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.summary-card {
  padding: 18px;
  border-radius: 24px;
}

.summary-card span {
  display: block;
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 30px;
  letter-spacing: -0.05em;
}

.summary-card.gold strong {
  color: #f1c979;
}

.report-card,
.insight-card {
  padding: 22px;
  border-radius: 28px;
  margin-bottom: 16px;
}

.report-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.report-header h2,
.section-heading h2 {
  margin: 0;
  font-size: 34px;
  line-height: 0.95;
  letter-spacing: -0.06em;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(241, 201, 121, 0.14);
  color: #f1c979;
  font-size: 12px;
  font-weight: 900;
}

.table-wrap {
  overflow-x: auto;
}

.analysis-table {
  width: 100%;
  min-width: 850px;
  border-collapse: collapse;
}

.analysis-table th {
  padding: 13px 12px;
  color: #b9aaa4;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  background: rgba(0, 0, 0, 0.22);
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.analysis-table td {
  padding: 14px 12px;
  color: #f7efe8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.075);
}

.analysis-table tr:hover td {
  background: rgba(255, 255, 255, 0.045);
}

.left-align {
  text-align: left;
}

.right-align {
  text-align: right;
}

.strong-cell {
  font-weight: 900;
}

.negative {
  color: #ff8e7a !important;
}

.empty-state {
  padding: 28px;
  border-radius: 24px;
  text-align: center;
  background: rgba(255, 255, 255, 0.045);
}

.empty-state h3 {
  margin: 0 0 8px;
  font-size: 26px;
  letter-spacing: -0.04em;
}

.empty-state p {
  margin: 0;
  color: #cfc2bd;
}

.insight-card p {
  margin: 12px 0 0;
  color: #d8cbc4;
  line-height: 1.55;
}

@media (max-width: 1100px) {
  .analysis-toolbar {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .summary-grid {
    grid-template-columns: repeat(2, minmax(160px, 1fr));
  }
}

@media (max-width: 760px) {
  .sales-analysis-shell {
    padding: 14px;
  }

  .analysis-hero,
  .report-header {
    flex-direction: column;
    align-items: stretch;
  }

  .analysis-toolbar,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .primary-action,
  .ghost-action {
    width: 100%;
  }
}
</style>